/**
 * @architecture-header 1
 * @layer module
 * @domain curriculum
 * @responsibility Load Classroom Curriculum units through the approved API boundary.
 * @state-owner TanStack Query
 * @allowed-imports app environment; authentication context; TanStack Query; Zod; module model
 * @forbidden routes; direct component API calls; Zustand server entities
 * @public-api via modules/curriculum/index.ts
 * @side-effects authenticated GET requests outside demo mode
 * @naming-hook GET -> use<Resource> (no verb); no useGet/useFetch/useLoad prefixes
 * @naming-path plural lowercase nouns; organization is the active-context scope hint
 * @naming-body camelCase; never caller role or server-side identity fields
 * @tests curriculum query tests
 */

import { useQuery } from '@tanstack/react-query';
import { z } from 'zod';
import { useWorkspaceEnvironment } from '../../../app/config/environment';
import { useActiveContext } from '../../auth';
import type {
  CurriculumOrganizationKnowledgeComponent,
  CurriculumStatus,
  CurriculumUnit,
} from '../model/types';

const curriculumCollectionPath = '/api/v1/classrooms';
const knowledgeComponentPath = '/api/v1/knowledge-components';

const componentSchema = z.object({
  id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  position: z.number().int().nonnegative(),
});

export const curriculumUnitSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  classroom_id: z.string().uuid(),
  name: z.string(),
  position: z.number().int().nonnegative(),
  revision: z.number().int().positive(),
  archived: z.boolean(),
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
  knowledge_components: z.array(componentSchema),
});

const curriculumListSchema = z.object({
  organization_id: z.string().uuid(),
  classroom_id: z.string().uuid(),
  status: z.enum(['active', 'archived']),
  items: z.array(curriculumUnitSchema),
});

export const knowledgeComponentSchema = z.object({
  id: z.string().uuid(),
  organization_id: z.string().uuid(),
  name: z.string(),
  description: z.string(),
  standard_code: z.string().nullable(),
  revision: z.number().int().positive(),
  archived: z.boolean(),
  archived_at: z.string().nullable(),
  created_at: z.string(),
  updated_at: z.string(),
});

const knowledgeComponentListSchema = z.object({
  organization_id: z.string().uuid(),
  status: z.enum(['active', 'archived']),
  items: z.array(knowledgeComponentSchema),
});

export const curriculumKeys = {
  all: (activeContextId: string, classroomId: string, status: CurriculumStatus) =>
    ['curriculum-units', activeContextId, classroomId, status] as const,
};

export const knowledgeComponentKeys = {
  all: (
    activeContextId: string,
    organizationId: string,
    status: CurriculumStatus,
    search: string,
  ) => ['knowledge-components', activeContextId, organizationId, status, search] as const,
};

interface CurriculumE2eFixture {
  readonly status: 'loading' | 'empty' | 'error' | 'denied' | 'ready';
  units?: CurriculumUnit[];
}

function fixture(): CurriculumE2eFixture | undefined {
  return (globalThis as typeof globalThis & { __VISPO_CUR_F01_FIXTURE__?: CurriculumE2eFixture })
    .__VISPO_CUR_F01_FIXTURE__;
}

/** Test/demo adapter used by the real query and mutation hooks in fixture mode. */
export function updateCurriculumFixture(
  updater: (units: readonly CurriculumUnit[]) => CurriculumUnit[],
): void {
  const current = fixture();
  if (!current) return;
  current.units = updater(current.units ?? []);
}

export function useCurriculumUnits(
  activeContextId: string,
  classroomId: string,
  status: CurriculumStatus = 'active',
) {
  const environment = useWorkspaceEnvironment();
  const organizationId = useActiveContext().data?.organizationId;
  return useQuery({
    queryKey: curriculumKeys.all(activeContextId, classroomId, status),
    enabled: Boolean(classroomId) && (environment.demoMode || Boolean(organizationId)),
    queryFn: async (): Promise<CurriculumUnit[]> => {
      if (environment.demoMode) {
        const current = fixture();
        if (current?.status === 'loading') return new Promise(() => undefined);
        if (current?.status === 'denied')
          throw Object.assign(new Error('Curriculum access denied'), { status: 403 });
        if (current?.status === 'error') throw new Error('Curriculum could not be loaded');
        if (current?.status === 'empty') return [];
        if (current?.units)
          return current.units
            .filter((unit) => (status === 'archived' ? unit.archived : !unit.archived))
            .sort((left, right) => left.position - right.position)
            .map((unit) => ({ ...unit }));
        return [];
      }
      if (!organizationId) throw new Error('目前沒有可用的老師工作區');
      const payload = await environment.apiClient.request<unknown>(
        `${curriculumCollectionPath}/${encodeURIComponent(classroomId)}/curriculum-units?organizationId=${encodeURIComponent(organizationId)}&status=${status}`,
      );
      return curriculumListSchema.parse(payload).items as CurriculumUnit[];
    },
    retry: 1,
  });
}

interface CurriculumKnowledgeComponentE2eFixture {
  readonly status: 'loading' | 'empty' | 'error' | 'denied' | 'ready';
  components?: CurriculumOrganizationKnowledgeComponent[];
}

function knowledgeComponentFixture(): CurriculumKnowledgeComponentE2eFixture | undefined {
  return (
    globalThis as typeof globalThis & {
      __VISPO_CUR_F03_FIXTURE__?: CurriculumKnowledgeComponentE2eFixture;
    }
  ).__VISPO_CUR_F03_FIXTURE__;
}

export function updateKnowledgeComponentFixture(
  updater: (
    components: readonly CurriculumOrganizationKnowledgeComponent[],
  ) => CurriculumOrganizationKnowledgeComponent[],
): void {
  const current = knowledgeComponentFixture();
  if (current) current.components = updater(current.components ?? []);
}

export function useKnowledgeComponents(
  activeContextId: string,
  organizationId: string,
  status: CurriculumStatus = 'active',
  search = '',
) {
  const environment = useWorkspaceEnvironment();
  return useQuery({
    queryKey: knowledgeComponentKeys.all(activeContextId, organizationId, status, search),
    enabled: Boolean(organizationId),
    queryFn: async (): Promise<CurriculumOrganizationKnowledgeComponent[]> => {
      if (environment.demoMode) {
        const current = knowledgeComponentFixture();
        if (current?.status === 'loading') return new Promise(() => undefined);
        if (current?.status === 'denied')
          throw Object.assign(new Error('Knowledge component access denied'), { status: 403 });
        if (current?.status === 'error')
          throw new Error('Knowledge components could not be loaded');
        const components = (current?.components ?? []).filter((component) =>
          status === 'archived' ? component.archived : !component.archived,
        );
        const normalized = search.trim().toLocaleLowerCase();
        return components
          .filter(
            (component) =>
              !normalized ||
              [component.name, component.standard_code ?? '', component.description].some((value) =>
                value.toLocaleLowerCase().includes(normalized),
              ),
          )
          .sort(
            (left, right) => left.name.localeCompare(right.name) || left.id.localeCompare(right.id),
          );
      }
      const payload = await environment.apiClient.request<unknown>(
        `${knowledgeComponentPath}?organizationId=${encodeURIComponent(organizationId)}&status=${status}&search=${encodeURIComponent(search.trim())}`,
      );
      return knowledgeComponentListSchema.parse(payload).items;
    },
    retry: 1,
  });
}

export function parseCurriculumUnit(value: unknown): CurriculumUnit {
  return curriculumUnitSchema.parse(value);
}
